import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, PauseCircle, Plus, Ship } from "lucide-react";
import {
  EVENT_CHOICES,
  useCallOperations,
  useOperationsBoard,
  useRecordEvent,
  type CallOperations,
  type EventKind,
  type Target,
} from "@/api/operations";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { PageHeader } from "@/components/ui/page-header";
import { StatTile } from "@/components/ui/stat-tile";
import { formatDate } from "@/lib/format";
import { useFormValues, useSubmitState } from "@/lib/use-form";
import { cn } from "@/lib/utils";

/** "in 3 h 20 m" / "4 h 10 m late" - the only number that matters at 3am. */
export function countdown(due: string, now: number): { text: string; late: boolean } {
  const minutes = Math.round((new Date(due).getTime() - now) / 60000);
  const late = minutes < 0;
  const total = Math.abs(minutes);
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  const span = hours > 0 ? `${hours} h ${rest} m` : `${rest} m`;
  return { text: late ? `${span} late` : `in ${span}`, late };
}

function targetTone(target: Target): string {
  if (target.state === "missed") return "text-destructive";
  if (target.state === "met") return "text-success";
  return "";
}

function TargetLine({ target, now }: { target: Target; now: number }) {
  const due = target.due_at ? countdown(target.due_at, now) : null;
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <span className={cn("font-medium", targetTone(target))}>{target.name}</span>
      <span className="text-xs text-muted-foreground">SLA {target.clause}</span>
      {target.state === "met" && <Badge variant="success">Met</Badge>}
      {target.state === "missed" && <Badge variant="destructive">Missed</Badge>}
      {target.state === "due" && due && (
        <span className={cn("text-xs", due.late ? "text-destructive" : "text-muted-foreground")}>
          {due.text}
        </span>
      )}
      {target.waited_minutes > 0 && (
        <span className="text-xs text-muted-foreground">
          (clock held {Math.round(target.waited_minutes / 60)} h while waiting)
        </span>
      )}
      {target.basis === "calendar_for_working" && (
        <span className="text-xs text-muted-foreground">counted in calendar hours</span>
      )}
    </li>
  );
}

function LogEventForm({ callId, onDone }: { callId: string; onDone: () => void }) {
  const record = useRecordEvent(callId);
  const { values, set } = useFormValues({
    kind: "load_list_received" as EventKind,
    happened_at: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16),
    note: "",
  });
  const { error, errors, saving, run } = useSubmitState();

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void run(async () => {
          await record.mutateAsync({
            kind: values.kind,
            happened_at: new Date(values.happened_at).toISOString(),
            note: values.note.trim(),
          });
          onDone();
        });
      }}
    >
      <RequestError error={error} />
      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          id={`${callId}-kind`}
          label="What happened"
          value={values.kind}
          onChange={(value) => set("kind", value as EventKind)}
          options={EVENT_CHOICES}
          error={errors.kind}
        />
        <TextField
          id={`${callId}-at`}
          label="When"
          type="datetime-local"
          value={values.happened_at}
          onChange={(value) => set("happened_at", value)}
          error={errors.happened_at}
          hint="When it actually happened - the SLA clocks measure from this"
        />
      </div>
      <TextAreaField
        id={`${callId}-note`}
        label="Note"
        value={values.note}
        onChange={(value) => set("note", value)}
        error={errors.note}
        rows={2}
        hint="For a planner assignment, the planner's name goes here"
      />
      <FormActions saving={saving} submitLabel="Log it" onCancel={onDone} />
    </form>
  );
}

function CallMail({ callId }: { callId: string }) {
  const detail = useCallOperations(callId);
  if (detail.isPending) return <p className="text-xs text-muted-foreground">Loading mail…</p>;
  if (detail.isError) return <RequestError error={detail.error} />;
  if (detail.data.mail.length === 0) {
    return <p className="text-xs text-muted-foreground">No e-mails filed under this call.</p>;
  }
  return (
    <ul className="flex flex-col gap-1 text-xs">
      {detail.data.mail.map((m) => (
        <li key={m.id}>
          <Link to={`/mail?tab=recent&message=${m.id}`} className="text-primary hover:underline">
            {m.subject || "(no subject)"}
          </Link>{" "}
          <span className="text-muted-foreground">
            {m.is_ours ? "we sent" : m.from_name || m.from_addr} · {formatDate(m.sent_at)}
            {m.attachment_count > 0 ? ` · ${m.attachment_count} file(s)` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function CallCard({ call, now }: { call: CallOperations; now: number }) {
  const [logging, setLogging] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const outstanding = call.targets.filter((target) => target.state === "due");
  const settled = call.targets.filter((target) => target.state !== "due");

  return (
    <Card className={cn(call.overdue_count > 0 && "border-destructive/60")}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <Link to={`/vessels`} className="hover:underline">
              {call.vessel_name || "Vessel"}
            </Link>
            {call.voyage && <span className="text-sm font-normal">voyage {call.voyage}</span>}
            <Badge variant={call.overdue_count > 0 ? "destructive" : "secondary"}>
              {call.stage_label}
            </Badge>
            {call.waiting && (
              <Badge variant="warning">
                <PauseCircle className="h-3 w-3" aria-hidden /> Waiting
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {call.invoice_no ? (
              <Link
                to={`/invoices/${call.invoice_id}`}
                className="text-sm text-primary hover:underline"
              >
                {call.invoice_no}
              </Link>
            ) : call.billable ? (
              <Link to="/invoices/new" className="text-sm text-primary hover:underline">
                Ready to invoice
              </Link>
            ) : (
              <span className="text-xs text-muted-foreground">Not billable yet</span>
            )}
            <Button size="sm" variant="outline" onClick={() => setLogging((open) => !open)}>
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Log
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {call.client_code} · {call.terminal} · {call.port} · arrives {formatDate(call.arrival)}
          {call.planner ? ` · planner ${call.planner}` : " · no planner named"}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {logging && <LogEventForm callId={call.vessel_call_id} onDone={() => setLogging(false)} />}

        {call.targets.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing is running yet. The clocks start when the load list arrives, and the MOVINS
            deadline needs the declared ETA.
          </p>
        ) : (
          <>
            {outstanding.length > 0 && (
              <ul className="flex flex-col gap-1">
                {outstanding.map((target, index) => (
                  <TargetLine key={`${target.name}-${index}`} target={target} now={now} />
                ))}
              </ul>
            )}
            {settled.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground">
                  {settled.length} done ({call.missed_count} missed)
                </summary>
                <ul className="mt-2 flex flex-col gap-1">
                  {settled.map((target, index) => (
                    <TargetLine key={`${target.name}-${index}`} target={target} now={now} />
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
        <details className="text-sm" onToggle={(e) => setShowMail(e.currentTarget.open)}>
          <summary className="cursor-pointer text-muted-foreground">E-mails</summary>
          {/* Fetched only once opened, so a board of ten calls does not make ten requests. */}
          <div className="mt-2">{showMail && <CallMail callId={call.vessel_call_id} />}</div>
        </details>
      </CardContent>
    </Card>
  );
}

export function OperationsPage() {
  const [includeDeparted, setIncludeDeparted] = useState(false);
  const board = useOperationsBoard({ includeDeparted });
  const now = Date.now();

  const calls = board.data ?? [];
  const late = calls.filter((call) => call.overdue_count > 0);
  const waiting = calls.filter((call) => call.waiting);
  const toInvoice = calls.filter((call) => call.billable && !call.invoice_no);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Operations"
        description="What each vessel call owes, and by when, under the service levels in the SLA."
      />

      {board.isError && <RequestError error={board.error} onReload={() => void board.refetch()} />}

      {calls.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <StatTile
            label="Past due"
            icon={AlertTriangle}
            value={late.length}
            tone={late.length > 0 ? "negative" : "default"}
          />
          <StatTile label="Waiting on others" icon={PauseCircle} value={waiting.length} />
          <StatTile label="Ready to invoice" icon={Clock} value={toInvoice.length} />
        </div>
      )}

      <Checkbox
        id="operations-departed"
        label="Show calls that have sailed"
        checked={includeDeparted}
        onChange={setIncludeDeparted}
      />

      {board.isPending ? (
        <p className="text-muted-foreground">Loading the board…</p>
      ) : calls.length === 0 ? (
        <EmptyState
          icon={Ship}
          title="No calls to watch"
          description="Add a vessel call, then log what happens as it happens."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {calls.map((call) => (
            <CallCard key={call.vessel_call_id} call={call} now={now} />
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Deadlines come from the SLA: the preliminary plan within 24 hours of a complete load
        list, a revision within 3 hours, the approved plan and MOVINS at least 12 hours before
        the declared ETA, emergency support acknowledged within the hour. Clocks are held while
        a call waits on the client, the vessel or the terminal (clause 5.1). The two limits the
        SLA states in working hours are counted here in calendar hours, which falls due earlier
        than promised, never later.
      </p>
    </div>
  );
}
